import { useId } from 'react';
import { Box, Button, CircularProgress, Modal, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { typography } from 'theme/tokens';

/**
 * The "No internet connection" page — shared by both portals, driven by props.
 *
 * ── An overlay, not a route swap ───────────────────────────────────────────
 * It is drawn ON TOP of the portal rather than in place of it. Underneath,
 * `AuthProvider` still holds an MFA / first-login rotation in progress, the
 * session keep-alive keeps ticking, and whatever the operator had typed is
 * still in its field. When the network returns the page simply unmounts and
 * they are back where they were — no reload.
 *
 * ── Why an MUI `Modal` and not a bare fixed Box ────────────────────────────
 * If an MUI `Dialog` is open when the outage hits (GST → Reset, booking
 * confirms…), its focus trap stays enabled for as long as it is the TOP modal
 * and pulls focus back into the now-invisible dialog on every `focusin` —
 * Tab walks its hidden buttons and Enter presses one blind. Rendering this
 * page as a `Modal` registers it with MUI's ModalManager as the top modal, so
 * the covered dialog's trap stands down. `Modal` also portals to
 * `document.body`, marks the covered console `aria-hidden`, keeps the body
 * from scrolling underneath, and restores focus to the field the operator
 * was in when the page hides.
 *
 * ── Colour comes from the ambient theme, never a literal ───────────────────
 * Both portals mount an MUI `ThemeProvider` at their root: the CRM's is
 * purple, the partner's is the partner's own primary. The icon tint is
 * `alpha(primary, 0.35)` — the same tint the two AccessDenied pages paint
 * with per-portal literals — derived here so one file serves both without
 * repainting a partner in Zenvoya purple.
 *
 * ── Busy state is `aria-disabled`, not `disabled` ──────────────────────────
 * A button that becomes `disabled` while focused drops focus to the body.
 * `aria-disabled` + a swallowed click keeps focus on Retry and reads as busy.
 *
 * ── Nothing here needs the network ─────────────────────────────────────────
 * No logo (the CRM's is a remote URL and partner logos are CDN uploads, both
 * unavailable offline), no AntD, no `Api/*`. It renders bare in Jest.
 */

/**
 * Above every kit layer (MUI tooltip 1500, AntD modals ~1000, the partner
 * mobile sidebar 1200) AND above react-hot-toast's default container (9999 —
 * a library default, not set in src/index.js), so the GENERIC_FAILURE toasts
 * from calls that failed offline do not stack on top of the explanation.
 */
export const NO_NETWORK_Z_INDEX = 10000;
export const NO_NETWORK_TITLE = 'No internet connection';
export const NO_NETWORK_DESCRIPTION =
  "Check your Wi-Fi or network cable. The console reconnects on its own once you're back online, or press Retry to check now.";
export const STILL_OFFLINE_HINT = 'Still offline. Check your connection and try again.';

/** The icon tint for whatever theme is ambient. Exported so the rule is testable without computed CSS. */
export const tintFor = (theme) => alpha(theme.palette.primary.main, 0.35);

export default function NoNetworkPage({ onRetry, checking = false, retryFailed = false }) {
  const titleId = useId();
  const descId = useId();

  return (
    <Modal open hideBackdrop disableEscapeKeyDown sx={{ zIndex: NO_NETWORK_Z_INDEX }}>
      <Box
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          overflowY: 'auto',
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 3,
          py: 4,
          // Opaque, and the same #F5F5F5 in both themes: nothing bleeds through.
          bgcolor: 'background.default',
        }}
      >
        <WifiOffRoundedIcon aria-hidden sx={{ fontSize: typography.fontSize[64], mb: 2, color: tintFor }} />
        <Typography id={titleId} variant="h4" component="h1" sx={{ fontWeight: typography.fontWeight.bold, mb: 1, color: 'text.primary' }}>
          {NO_NETWORK_TITLE}
        </Typography>
        <Typography id={descId} sx={{ color: 'text.secondary', mb: 3, maxWidth: 560 }}>
          {NO_NETWORK_DESCRIPTION}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          autoFocus
          aria-disabled={checking}
          aria-busy={checking}
          onClick={checking ? undefined : onRetry}
          startIcon={checking ? <CircularProgress size={16} color="inherit" /> : <RefreshRoundedIcon />}
          sx={{ minWidth: 140 }}
        >
          {checking ? 'Checking…' : 'Retry'}
        </Button>
        {/* Always mounted: a live region has to exist BEFORE its text changes
            for a screen reader to announce it. Hidden while checking again so
            a new attempt does not read as a failure. */}
        <Typography role="status" aria-live="polite" sx={{ mt: 2, minHeight: 20, fontSize: typography.fontSize[13], color: 'text.secondary' }}>
          {retryFailed && !checking ? STILL_OFFLINE_HINT : ''}
        </Typography>
      </Box>
    </Modal>
  );
}
